'use client'

import { Wave } from '@/app/_components/Wave'
import { cn } from '@/app/_primitives/utils/cn'
import { useCallback, useEffect, useRef, useState } from 'react'
import Slider from 'react-slick'
import { twMerge } from 'tailwind-merge'
import ChevronBackward from '../Icon/ChevronBackward'
import ChevronForward from '../Icon/ChevronForward'

export type Slides = {
  id: number
  content: JSX.Element
}

type CarouselProps = {
  contents?: Slides[]
  waveColor?: string
  autoPlay?: boolean
  centerMode?: boolean
  onChange?: (index: number) => void
}

export default function Carousel({
  contents,
  autoPlay = true,
  waveColor = 'white',
  centerMode = false,
  onChange,
}: CarouselProps) {
  const [activeSlide, setActiveSlide] = useState(0)

  const slickRef = useRef<Slider>(null)
  const onClickPrevious = useCallback(() => slickRef.current!.slickPrev(), [])
  const onClickNext = useCallback(() => slickRef.current!.slickNext(), [])

  useEffect(() => {
    if (slickRef.current && onChange) {
      onChange(0)
    }
    // eslint-disable-next-line
  }, [slickRef])

  const settings = {
    // @ts-expect-error content is possibly undefined
    dots: contents.length !== 1 && !centerMode,
    infinite: (contents?.length || 0) > 1,
    speed: 500,
    centerMode: centerMode,
    slidesToShow: 1,
    centerPadding: centerMode ? '148px' : '0px',
    slidesToScroll: 1,
    useTransform: false,
    autoplay: autoPlay,
    autoplaySpeed: 3500,
    cssEase: 'linear',
    beforeChange: (_: number, next: number) => {
      setActiveSlide(next)
      try {
        onChange?.(next)
      } catch (e) {
        console.error(e)
      }
    },
    // eslint-disable-next-line
    customPaging: (index: number) => (
      <div className="absolute bottom-70 z-10 flex gap-10">
        <div
          className={twMerge(
            `h-8 w-8 rounded-full`,
            `${index === activeSlide ? `bg-[#D9D9D9]/80` : `bg-[#D9D9D9]/20`}`,
          )}
        />
      </div>
    ),
  }

  return (
    <div className="relative size-full">
      <div className="collabo-item flex size-full items-center">
        <Slider
          {...settings}
          ref={slickRef}
          className="relative box-border size-full overflow-hidden">
          {contents?.map((data) => (
            <div
              key={data.id}
              className={cn({
                'opacity-30': data.id !== activeSlide && centerMode,
              })}>
              {data.content}
            </div>
          ))}
        </Slider>

        {contents && contents.length > 1 && (
          <div className="pointer-events-none absolute inset-x-0 z-10 flex w-full items-center xl:container">
            <button
              type="button"
              className="pointer-events-auto absolute size-24 cursor-pointer max-lg:hidden lg:left-20"
              onClick={onClickPrevious}>
              <ChevronBackward fillOpacity={0.4} fill={'#FFFFFF'} />
            </button>
            <button
              type="button"
              className="pointer-events-auto absolute size-24 cursor-pointer max-lg:hidden lg:right-20"
              onClick={onClickNext}>
              <ChevronForward fillOpacity={0.4} fill={'#FFFFFF'} />
            </button>
          </div>
        )}
      </div>
      <div className="z-8 absolute bottom-0 w-full">
        <Wave type="wave5" fill={waveColor} backgroundColor="transparent" />
      </div>
    </div>
  )
}
